/**
 * ShowSourceArchiveModal — Spec arch §16.4 (Forensic provenance view).
 *
 * Per-imported-message chain-of-custody surface. Calls
 * GET /api/edgemail/admin/migration/messages/:messageId/source-archive-view
 * (wired in PR 15) and renders the resulting `view` object.
 *
 * Where this lives in the UI today:
 *   - PR 15 mounts it inside MigrationJobDetailPanel via an "Inspect message"
 *     control (admin pastes a message_id) so we have a way to demo the
 *     forensic view from the Migration tab.
 *   - The natural long-term home is the EdgeMail web client itself — a
 *     "Show source archive" item in the per-message three-dot menu — which
 *     belongs to a future EdgeMail-client PR, not to admin scope.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  XMarkIcon,
  DocumentMagnifyingGlassIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { EdgeMailAdminAPI } from '../../services/EdgeMailAdminAPI';
import { migrationQueryKeys } from '../../hooks/useMigrationJobs';

interface SourceArchiveAttachmentHash {
  filename: string;
  sha256: string;
  size_bytes: number;
  gcs_path: string;
}

interface SourceArchiveView {
  message_id: string;
  source_path: string | null;
  source_storage_path: string | null;
  imported_storage_path: string | null;
  hashes: {
    edrm_mih: string | null;
    fallback_hash: string | null;
    attachments: SourceArchiveAttachmentHash[];
  };
  diff_summary: {
    canonical_body_differs_from_source: boolean | null;
    stripped_headers: string[];
    charset_re_encoded: boolean;
  };
  migration_job_id: string | null;
  imported_at: string;
  message_id_header: string | null;
  subject: string | null;
}

interface ShowSourceArchiveModalProps {
  messageId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export function ShowSourceArchiveModal({ messageId, isOpen, onClose }: ShowSourceArchiveModalProps) {
  const [copied, setCopied] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: messageId ? migrationQueryKeys.sourceArchiveView(messageId) : ['source-archive-disabled'],
    queryFn: () => EdgeMailAdminAPI.getMigrationSourceArchiveView(messageId as string),
    enabled: isOpen && !!messageId,
  });

  if (!isOpen) return null;
  const view = (data as { view: SourceArchiveView } | null | undefined)?.view ?? (data as SourceArchiveView | undefined);

  const copy = (label: string, value: string | null | undefined) => {
    if (!value) return;
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] flex flex-col bg-white dark:bg-gray-900 rounded-lg shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <DocumentMagnifyingGlassIcon className="h-5 w-5 text-lex-accent" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Source archive view</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5 text-sm space-y-4">
          {isLoading && (
            <div className="text-center py-8 text-gray-500">
              <ArrowPathIcon className="h-5 w-5 mx-auto animate-spin" />
              <div className="mt-2 text-sm">Loading forensic view…</div>
            </div>
          )}
          {isError && (
            <div className="flex items-start gap-2 text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/20 p-3 rounded">
              <ExclamationCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error instanceof Error ? error.message : 'Failed to load view'}</span>
            </div>
          )}
          {view && (
            <>
              <Section title="Message">
                <Row label="Message ID" value={view.message_id} mono onCopy={() => copy('Message ID', view.message_id)} copied={copied === 'Message ID'} />
                <Row label="Subject" value={view.subject ?? '(none)'} />
                <Row label="RFC Message-ID" value={view.message_id_header ?? '(none)'} mono />
                <Row label="Imported at" value={new Date(view.imported_at).toLocaleString()} />
                <Row label="Migration job" value={view.migration_job_id ?? '(none)'} mono />
              </Section>

              <Section title="Source archive locator">
                <Row label="Source path" value={view.source_path ?? '(unknown)'} mono onCopy={() => copy('Source path', view.source_path)} copied={copied === 'Source path'} />
                <Row label="Source storage" value={view.source_storage_path ?? '(none)'} mono onCopy={() => copy('Source storage', view.source_storage_path)} copied={copied === 'Source storage'} />
              </Section>

              <Section title="Imported bytes">
                <Row label="Canonical MIME" value={view.imported_storage_path ?? '(none)'} mono onCopy={() => copy('Imported MIME path', view.imported_storage_path)} copied={copied === 'Imported MIME path'} />
              </Section>

              <Section title="Hash chain">
                <Row label="EDRM MIH (md5)" value={view.hashes.edrm_mih ?? '(no Message-ID)'} mono />
                <Row label="Fallback hash (sha256)" value={view.hashes.fallback_hash ?? '(none)'} mono />
                {view.hashes.attachments.length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs font-medium text-gray-500 mb-1">Attachments</div>
                    <ul className="space-y-1 text-xs font-mono">
                      {view.hashes.attachments.map((a, i) => (
                        <li key={i} className="text-gray-700 dark:text-gray-300">
                          <span className="text-gray-500">{a.filename}</span> · {a.size_bytes.toLocaleString()} B · sha256={a.sha256 || '(unrecorded)'}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Section>

              <Section title="Diff summary">
                <Row label="Charset re-encoded" value={view.diff_summary.charset_re_encoded ? 'Yes' : 'No'} />
                <Row label="Headers stripped" value={view.diff_summary.stripped_headers.join(', ') || 'None'} />
                <Row
                  label="Body differs from source"
                  value={
                    view.diff_summary.canonical_body_differs_from_source === null
                      ? 'Not yet computed (PR 22 hardening)'
                      : view.diff_summary.canonical_body_differs_from_source
                        ? 'Yes'
                        : 'No'
                  }
                />
              </Section>
            </>
          )}
        </div>

        <div className="flex items-center justify-end p-4 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Atoms ───────────────────────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-gray-200 dark:border-gray-800 pt-3 first:border-t-0 first:pt-0">
      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{title}</h3>
      {children}
    </div>
  );
}

function Row({
  label, value, mono, onCopy, copied,
}: {
  label: string; value: string; mono?: boolean; onCopy?: () => void; copied?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 py-1">
      <div className="w-44 text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`flex-1 break-all text-gray-900 dark:text-white ${mono ? 'font-mono text-xs' : 'text-sm'}`}>{value}</div>
      {onCopy && (
        <button
          onClick={onCopy}
          title={copied ? 'Copied' : 'Copy'}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <ClipboardDocumentIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export default ShowSourceArchiveModal;
